import React, { useState, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import {
  SandpackProvider,
  SandpackLayout,
  SandpackCodeEditor,
  SandpackPreview,
  SandpackFileExplorer,
  SandpackConsole,
  useSandpack,
} from '@codesandbox/sandpack-react';

type EditorMode = 'javascript' | 'react';

interface OutputItem {
  type: 'input' | 'output' | 'error' | 'warn' | 'info' | 'system';
  text: string;
}

interface CodeData {
  currentCode: string;
  codeHistory: string;
  output: string;
  timestamp: string;
}

interface CodeEditorProps {
  onCodeSubmit?: (codeData: CodeData) => void;
  onSendToAI?: () => void;
  connectionId: string | null;
  ws: WebSocket | null;
}

// ─── Default files for React playground ───────────────────────────

const DEFAULT_REACT_FILES: Record<string, { code: string }> = {
  '/App.js': {
    code: `import { useState } from "react";
import "./styles.css";

export default function App() {
  const [count, setCount] = useState(0);

  return (
    <div className="app">
      <h1>Hello React</h1>
      <p>Count: {count}</p>
      <div className="buttons">
        <button onClick={() => setCount((c) => c + 1)}>Increment</button>
        <button onClick={() => setCount(0)}>Reset</button>
      </div>
    </div>
  );
}
`,
  },
  '/styles.css': {
    code: `.app {
  font-family: system-ui, -apple-system, sans-serif;
  padding: 24px;
  max-width: 600px;
  margin: 0 auto;
}
h1 { color: #1e293b; }
.buttons { display: flex; gap: 8px; margin-top: 12px; }
button {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  cursor: pointer;
  font-size: 14px;
}
button:hover { background: #e2e8f0; }
`,
  },
};

const JS_STARTER =
  '// Write your JavaScript code here\n// Click "Run" to execute or "Send to AI" to share with interviewer\n\n';

// ─── FileSyncer: keeps a ref in sync with Sandpack internal files ─

const FileSyncer: React.FC<{
  filesRef: React.MutableRefObject<Record<string, { code: string }>>;
}> = ({ filesRef }) => {
  const { sandpack } = useSandpack();
  const mapped: Record<string, { code: string }> = {};
  for (const [path, file] of Object.entries(sandpack.files)) {
    mapped[path] = { code: file.code };
  }
  filesRef.current = mapped;
  return null;
};

// ─── React Playground Toolbar (inside SandpackProvider) ───────────

interface ReactToolbarProps {
  ws: WebSocket | null;
  connectionId: string | null;
  onCodeSubmit?: (codeData: CodeData) => void;
  onSendToAI?: () => void;
  deps: Record<string, string>;
  onAddDep: (pkg: string) => void;
  onRemoveDep: (pkg: string) => void;
}

const ReactPlaygroundToolbar: React.FC<ReactToolbarProps> = ({
  ws,
  connectionId,
  onCodeSubmit,
  onSendToAI,
  deps,
  onAddDep,
  onRemoveDep,
}) => {
  const { sandpack } = useSandpack();
  const [pkgInput, setPkgInput] = useState('');
  const [newFileInput, setNewFileInput] = useState('');
  const [showNewFile, setShowNewFile] = useState(false);

  const sendToAI = () => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      alert('Not connected to interview session.');
      return;
    }
    if (!connectionId) {
      alert('Connection ID not available.');
      return;
    }

    const allCode = Object.entries(sandpack.files)
      .filter(
        ([p]) => !p.includes('node_modules') && !p.includes('/package.json')
      )
      .map(([p, f]) => `// ===== ${p} =====\n${f.code}`)
      .join('\n\n');

    const codeData: CodeData = {
      currentCode: sandpack.files[sandpack.activeFile]?.code || '',
      codeHistory: allCode,
      output: `React Playground — ${Object.keys(sandpack.files).length} files\nDependencies: ${Object.keys(deps).join(', ') || 'none'}`,
      timestamp: new Date().toISOString(),
    };

    try {
      ws.send(
        JSON.stringify({
          type: 'terminal_output',
          terminal_data: codeData,
          connection_id: connectionId,
        })
      );
      if (onCodeSubmit) onCodeSubmit(codeData);
      if (onSendToAI) onSendToAI();
    } catch (err) {
      alert(
        `Failed to send: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  };

  const addPkg = () => {
    const pkg = pkgInput.trim();
    if (!pkg) return;
    onAddDep(pkg);
    setPkgInput('');
  };

  const addFile = () => {
    let path = newFileInput.trim();
    if (!path) return;
    if (!path.startsWith('/')) path = '/' + path;
    if (!path.includes('.')) path += '.js';
    sandpack.addFile(path, '');
    sandpack.setActiveFile(path);
    setNewFileInput('');
    setShowNewFile(false);
  };

  return (
    <div className="mb-2 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Package install */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-400">📦</span>
          <input
            className="w-36 sm:w-44 px-2 py-1 text-xs border border-slate-600 rounded bg-slate-800 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-indigo-400"
            placeholder="e.g. axios, lodash"
            value={pkgInput}
            onChange={(e) => setPkgInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addPkg()}
          />
          <button
            className="px-2 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600 font-medium"
            onClick={addPkg}
          >
            Install
          </button>
        </div>

        {/* New file */}
        {showNewFile ? (
          <div className="flex items-center gap-1">
            <input
              className="w-32 px-2 py-1 text-xs border border-slate-600 rounded bg-slate-800 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-indigo-400"
              placeholder="/Component.js"
              value={newFileInput}
              onChange={(e) => setNewFileInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addFile();
                if (e.key === 'Escape') setShowNewFile(false);
              }}
              autoFocus
            />
            <button
              className="px-2 py-1 text-xs bg-emerald-500 text-white rounded font-medium"
              onClick={addFile}
            >
              Add
            </button>
            <button
              className="px-1.5 py-1 text-xs bg-slate-600 text-slate-300 rounded"
              onClick={() => setShowNewFile(false)}
            >
              ✕
            </button>
          </div>
        ) : (
          <button
            className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600 font-medium"
            onClick={() => setShowNewFile(true)}
          >
            + File
          </button>
        )}

        <div className="flex-1" />

        <button
          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded font-semibold hover:bg-blue-700"
          onClick={sendToAI}
        >
          📤 Send to AI
        </button>
      </div>

      {/* Installed packages */}
      {Object.keys(deps).length > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          {Object.entries(deps).map(([pkg]) => (
            <span
              key={pkg}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-900/50 text-indigo-300 text-xs rounded-full border border-indigo-700/50"
            >
              {pkg}
              <button
                className="text-indigo-400 hover:text-red-400 leading-none"
                onClick={() => onRemoveDep(pkg)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── JS Editor (Monaco-based, for plain JavaScript) ──────────────

interface JsEditorProps {
  onCodeSubmit?: (codeData: CodeData) => void;
  onSendToAI?: () => void;
  connectionId: string | null;
  ws: WebSocket | null;
  onSwitchMode: () => void;
}

const JsEditor: React.FC<JsEditorProps> = ({
  onCodeSubmit,
  onSendToAI,
  connectionId,
  ws,
  onSwitchMode,
}) => {
  const [code, setCode] = useState<string>(JS_STARTER);
  const [output, setOutput] = useState<OutputItem[]>([
    { type: 'system', text: 'JavaScript Code Editor — Ready!\n' },
  ]);
  const [isRunning, setIsRunning] = useState(false);
  const outputEndRef = useRef<HTMLDivElement>(null);
  const codeHistoryRef = useRef<string[]>([]);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [output]);

  const executeCode = (codeToRun?: string) => {
    const src = codeToRun ?? code;
    if (!src.trim() || isRunning) return;
    setIsRunning(true);
    setOutput((p) => [...p, { type: 'input', text: '> Running code...\n' }]);
    codeHistoryRef.current.push(src);

    try {
      const escaped = src
        .replace(/\\/g, '\\\\')
        .replace(/`/g, '\\`')
        .replace(/\${/g, '\\${');
      const body = `
        const logs=[];const oL=console.log,oE=console.error,oW=console.warn,oI=console.info;
        function s(v){if(v===null)return'null';if(v===undefined)return'undefined';if(typeof v==='object')try{return JSON.stringify(v,null,2)}catch(e){return String(v)}return String(v)}
        console.log=function(){logs.push({t:'log',a:Array.from(arguments).map(s)});oL.apply(console,arguments)};
        console.error=function(){logs.push({t:'error',a:Array.from(arguments).map(s)});oE.apply(console,arguments)};
        console.warn=function(){logs.push({t:'warn',a:Array.from(arguments).map(s)});oW.apply(console,arguments)};
        console.info=function(){logs.push({t:'info',a:Array.from(arguments).map(s)});oI.apply(console,arguments)};
        let r;try{r=(function(){${escaped}})()}catch(e){logs.push({t:'error',a:['Error: '+e.message]});throw e}
        return {r,logs};`;
      const result = new Function(body)() as {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        r: any;
        logs: Array<{ t: string; a: string[] }>;
      };
      if (result.logs.length > 0) {
        result.logs.forEach((l) =>
          setOutput((p) => [
            ...p,
            {
              type:
                l.t === 'error'
                  ? 'error'
                  : l.t === 'warn'
                    ? 'warn'
                    : 'output',
              text: l.a.join(' '),
            },
          ])
        );
      } else if (result.r !== undefined) {
        setOutput((p) => [
          ...p,
          {
            type: 'output',
            text:
              typeof result.r === 'object'
                ? JSON.stringify(result.r, null, 2)
                : String(result.r),
          },
        ]);
      } else {
        setOutput((p) => [
          ...p,
          { type: 'output', text: 'Code executed (no output)' },
        ]);
      }
      setOutput((p) => [
        ...p,
        { type: 'system', text: '\n✓ Execution completed\n' },
      ]);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setOutput((p) => [
        ...p,
        { type: 'error', text: `Error: ${msg}` },
        { type: 'system', text: '\n✗ Execution failed\n' },
      ]);
    } finally {
      setIsRunning(false);
    }
  };

  const sendCodeToAI = () => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      alert('Not connected to interview session.');
      return;
    }
    if (!connectionId || !code.trim()) return;

    const codeData: CodeData = {
      currentCode: code,
      codeHistory:
        codeHistoryRef.current.length > 0
          ? [...codeHistoryRef.current, code].join('\n\n---\n\n')
          : code,
      output: output.map((i) => i.text).join('\n'),
      timestamp: new Date().toISOString(),
    };
    try {
      ws.send(
        JSON.stringify({
          type: 'terminal_output',
          terminal_data: codeData,
          connection_id: connectionId,
        })
      );
      setOutput((p) => [
        ...p,
        { type: 'system', text: '\n[Code sent to AI interviewer]\n' },
      ]);
      if (onCodeSubmit) onCodeSubmit(codeData);
      if (onSendToAI) onSendToAI();
    } catch (err) {
      alert(
        `Failed to send: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  };

  const outColor = (t: string) => {
    const b = 'whitespace-pre-wrap break-words mb-0.5';
    if (t === 'error') return `${b} text-red-400`;
    if (t === 'warn') return `${b} text-yellow-300`;
    if (t === 'info') return `${b} text-blue-400`;
    if (t === 'system') return `${b} text-slate-500 italic`;
    if (t === 'input') return `${b} text-teal-400`;
    return `${b} text-slate-200`;
  };

  return (
    <div className="bg-white rounded-xl p-4 flex flex-col flex-1 min-h-0 shadow-lg">
      <div className="flex justify-between items-center mb-3 pb-3 border-b-2 border-slate-200 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h3 className="m-0 text-slate-800 text-lg font-semibold">
            JavaScript Editor
          </h3>
          <div className="flex bg-slate-100 rounded-md p-0.5 text-xs">
            <button className="px-3 py-1 rounded font-medium bg-white shadow text-slate-800">
              JS
            </button>
            <button
              className="px-3 py-1 rounded font-medium text-slate-500 hover:text-slate-700"
              onClick={onSwitchMode}
            >
              React
            </button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            className={`px-3 py-1.5 rounded-md text-sm font-semibold border-none cursor-pointer ${isRunning || !code.trim() ? 'bg-slate-400 text-white cursor-not-allowed' : 'bg-emerald-500 text-white hover:bg-emerald-600'}`}
            onClick={() => executeCode()}
            disabled={isRunning || !code.trim()}
          >
            ▶ Run
          </button>
          <button
            className="px-3 py-1.5 bg-slate-500 text-white rounded-md text-sm font-semibold cursor-pointer hover:bg-slate-600"
            onClick={() => setOutput([{ type: 'system', text: 'Cleared.\n' }])}
          >
            Clear
          </button>
          <button
            className={`px-3 py-1.5 rounded-md text-sm font-semibold border-none cursor-pointer ${!code.trim() ? 'bg-slate-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            onClick={sendCodeToAI}
            disabled={!code.trim()}
          >
            📤 Send to AI
          </button>
        </div>
      </div>

      <div className="flex-1 flex gap-3 min-h-0">
        <div className="flex-1 flex flex-col min-w-0 border-2 border-slate-200 rounded-lg overflow-hidden">
          <Editor
            height="100%"
            language="javascript"
            value={code}
            onChange={(v) => setCode(v || '')}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
              wordWrap: 'on',
              bracketPairColorization: { enabled: true },
              guides: { bracketPairs: true },
              folding: true,
              matchBrackets: 'always',
              smoothScrolling: true,
            }}
            onMount={(ed, monaco) => {
              editorRef.current = ed;
              ed.addCommand(
                monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
                () => {
                  const c = ed.getValue();
                  setCode(c);
                  executeCode(c);
                }
              );
            }}
          />
        </div>

        <div className="flex-1 flex flex-col min-w-0 border-2 border-slate-200 rounded-lg overflow-hidden bg-slate-900">
          <div className="px-3 py-1.5 bg-slate-800 text-slate-200 font-semibold text-xs border-b border-slate-700">
            Output
          </div>
          <div className="flex-1 p-2 overflow-y-auto font-mono text-xs leading-relaxed">
            {output.map((item, i) => (
              <div key={i} className={outColor(item.type)}>
                {item.text}
              </div>
            ))}
            <div ref={outputEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main CodeEditor component ───────────────────────────────────

const CodeEditor: React.FC<CodeEditorProps> = ({
  onCodeSubmit,
  onSendToAI,
  connectionId,
  ws,
}) => {
  const [mode, setMode] = useState<EditorMode>('javascript');
  const [deps, setDeps] = useState<Record<string, string>>({});
  const [providerKey, setProviderKey] = useState(0);
  const filesRef = useRef<Record<string, { code: string }>>(
    DEFAULT_REACT_FILES
  );
  const wsRef = useRef(ws);

  useEffect(() => {
    wsRef.current = ws;
  }, [ws]);

  const addDep = (pkg: string) => {
    setDeps((prev) => {
      const updated = { ...prev, [pkg]: 'latest' };
      setTimeout(() => setProviderKey((k) => k + 1), 0);
      return updated;
    });
  };

  const removeDep = (pkg: string) => {
    setDeps((prev) => {
      const updated = { ...prev };
      delete updated[pkg];
      setTimeout(() => setProviderKey((k) => k + 1), 0);
      return updated;
    });
  };

  if (mode === 'javascript') {
    return (
      <JsEditor
        onCodeSubmit={onCodeSubmit}
        onSendToAI={onSendToAI}
        connectionId={connectionId}
        ws={ws}
        onSwitchMode={() => setMode('react')}
      />
    );
  }

  return (
    <div className="bg-slate-900 rounded-xl p-4 flex flex-col flex-1 min-h-0 shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-700">
        <h3 className="m-0 text-white text-lg font-semibold">
          React Playground
        </h3>
        <div className="flex bg-slate-800 rounded-md p-0.5 text-xs">
          <button
            className="px-3 py-1 rounded font-medium text-slate-400 hover:text-white"
            onClick={() => setMode('javascript')}
          >
            JS
          </button>
          <button className="px-3 py-1 rounded font-medium bg-indigo-600 text-white shadow">
            React
          </button>
        </div>
      </div>

      {/* Sandpack Provider */}
      <SandpackProvider
        key={providerKey}
        template="react"
        theme="dark"
        files={filesRef.current}
        customSetup={{
          dependencies: deps,
        }}
        options={{
          recompileMode: 'delayed',
          recompileDelay: 400,
          autorun: true,
          autoReload: true,
        }}
      >
        <FileSyncer filesRef={filesRef} />
        <ReactPlaygroundToolbar
          ws={wsRef.current}
          connectionId={connectionId}
          onCodeSubmit={onCodeSubmit}
          onSendToAI={onSendToAI}
          deps={deps}
          onAddDep={addDep}
          onRemoveDep={removeDep}
        />
        <SandpackLayout
          style={{
            flex: 1,
            minHeight: 0,
            borderRadius: '0.5rem',
            overflow: 'hidden',
            border: '1px solid #334155',
          }}
        >
          <SandpackFileExplorer
            style={{ minWidth: 140, maxWidth: 170 }}
            autoHiddenFiles
          />
          <SandpackCodeEditor
            style={{ flex: 1, minWidth: 0 }}
            showLineNumbers
            showTabs
            closableTabs
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minWidth: 0,
            }}
          >
            <SandpackPreview
              style={{ flex: 1 }}
              showRefreshButton
              showOpenInCodeSandbox={false}
            />
            <SandpackConsole
              style={{
                height: 140,
                borderTop: '1px solid #334155',
              }}
            />
          </div>
        </SandpackLayout>
      </SandpackProvider>
    </div>
  );
};

export default CodeEditor;
